<template>
  <span class="player-name">
    <span v-if="isLoading" class="name-loading" title="Loading name">
      <span class="spinner-border spinner-border-sm" role="status"></span>
      <span class="visually-hidden">Loading name...</span>
    </span>

    <router-link v-else-if="isNavigable" :to="{ name: 'Player', params: { name: displayName } }" class="name-link">
      {{ displayName }}
    </router-link>

    <span v-else class="name-text" :class="{ 'name-muted': !resolvedName }">{{ displayName }}</span>
  </span>
</template>

<script>
import PlayerNames, { PLAYER_NAME_STATUS } from '@/service/playernames.js'

export default {
  name: 'KopPlayerName',

  props: {
    playerId: {
      type: [Number, String],
      required: true,
    },
    // Matches used to carry names inline. While the API still sends them we
    // render that value and skip the lookup entirely.
    fallbackName: {
      type: String,
      default: '',
    },
    link: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    isBot() {
      return PlayerNames.isBot(this.playerId)
    },

    status() {
      return PlayerNames.getStatus(this.playerId)
    },

    resolvedName() {
      return this.fallbackName || PlayerNames.getName(this.playerId)
    },

    // Idle counts as pending: a name that has not been asked for yet is either
    // off screen, or on screen with the observer about to fire. Showing
    // "Unknown" in either case would flash the wrong answer.
    isLoading() {
      if (this.resolvedName || this.isBot) return false
      return this.status === PLAYER_NAME_STATUS.idle || this.status === PLAYER_NAME_STATUS.loading
    },

    displayName() {
      if (this.resolvedName) return this.resolvedName
      if (this.isBot) return 'Bot'
      return 'Unknown'
    },

    isNavigable() {
      return this.link && Boolean(this.resolvedName)
    },
  },

  watch: {
    playerId: 'resolve',
  },

  created() {
    this.resolve()
  },

  methods: {
    // Every row that renders asks for its name. Requests raised together are
    // batched by the service, so one page of matches is one round trip, and
    // loading more matches only asks for the ids that are new.
    resolve() {
      if (this.isBot || this.fallbackName) return
      PlayerNames.request(this.playerId)
    },
  },
}
</script>

<style scoped>
.player-name {
  display: inline-flex;
  align-items: center;
  min-height: 1.2em;
}

.name-loading {
  display: inline-flex;
  align-items: center;
  color: #9fb4ba;
}

.spinner-border-sm {
  width: 0.85rem;
  height: 0.85rem;
  border-width: 0.14em;
}

.name-link {
  color: inherit;
  text-decoration: none;
}

.name-link:hover {
  color: #ffbf66;
  text-decoration: underline;
}

.name-muted {
  color: #9fb4ba;
}
</style>
